
import { useState } from 'react';

const platforms = [
  {
    id: 'meta',
    name: 'Meta 生態系',
    subtitle: 'Facebook & Instagram',
    tag: '視覺痛點觸發',
    icon: 'ri-facebook-circle-fill',
    secondIcon: 'ri-instagram-fill',
    accentColor: '#E1306C',
    bgGlow: 'from-[#4267B2]/20 to-[#E1306C]/20',
    borderHover: 'hover:border-[#E1306C]/50',
    tagBg: 'bg-[#E1306C]/10',
    tagText: 'text-[#E1306C]',
    description:
      '利用精準的興趣標籤與類似受眾 (Lookalike) 演算法，將具備商業侵略性的影音素材推播至高淨值客群的動態牆，瞬間創造需求。',
  },
  {
    id: 'tiktok',
    name: 'TikTok 演算法突圍',
    subtitle: '短影音矩陣',
    tag: '破壞式流量攔截',
    icon: 'ri-tiktok-fill',
    secondIcon: '',
    accentColor: '#FE2C55',
    bgGlow: 'from-[#25F4EE]/15 to-[#FE2C55]/15',
    borderHover: 'hover:border-[#FE2C55]/50',
    tagBg: 'bg-[#FE2C55]/10',
    tagText: 'text-[#FE2C55]',
    description:
      '針對高成癮性的短影音紅利，策劃高完播率與高互動的短影音腳本。在注意力最集中的黃金三秒內，完成品牌曝光與高效引流。',
  },
  {
    id: 'google',
    name: 'Google Ads',
    subtitle: '搜尋意圖捕捉',
    tag: '高誠意商機收割',
    icon: 'ri-google-fill',
    secondIcon: '',
    accentColor: '#EA4335',
    bgGlow: 'from-[#4285F4]/15 to-[#EA4335]/15',
    borderHover: 'hover:border-[#FBBC05]/50',
    tagBg: 'bg-[#FBBC05]/15',
    tagText: 'text-[#E37400]',
    description:
      '佈局精準的長尾關鍵字與最高成效廣告 (PMax)。當客戶已經產生具體問題並主動搜尋時，確保您的防禦型漏斗出現在第一順位。',
  },
  {
    id: 'line',
    name: 'LINE 商業生態圈',
    subtitle: 'LAP & API 串接',
    tag: '台灣商務終極閉環',
    icon: 'ri-line-fill',
    secondIcon: '',
    accentColor: '#06C755',
    bgGlow: 'from-[#06C755]/15 to-[#06C755]/10',
    borderHover: 'hover:border-[#06C755]/50',
    tagBg: 'bg-[#06C755]/10',
    tagText: 'text-[#06C755]',
    description:
      '不僅透過 LINE 廣告觸及在地客群，更透過 API 將前端收集到的「合格商機」，於 1 秒內無縫派發至您的 LINE 終端，鎖定最燙手的成交時機。',
  },
];

export default function TrafficMatrix() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <section className="relative py-28 overflow-hidden bg-[#0B1120]">
      {/* Background grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#1E3A8A]/8 blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 px-5 py-2 rounded-full text-sm font-medium mb-6">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-global-line text-sm"></i>
            </div>
            <span>Omnichannel Traffic Matrix</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-6">全網精準打擊矩陣</h2>
          <p className="text-lg text-white/60 leading-relaxed max-w-3xl mx-auto">
            我們不依賴單一渠道。<strong className="text-white/90">全鏈獲客整合行銷</strong>整合全球頂尖演算法平台，佈下天羅地網。從主動搜尋到被動觸及，全方位攔截您的具備真實購買力的高意願受眾。
          </p>
        </div>

        {/* Central Funnel + Platform Grid */}
        <div className="relative">
          {/* Central Funnel Element */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none hidden lg:flex flex-col items-center">
            {/* Outer pulse ring */}
            <div
              className="absolute w-40 h-40 rounded-full border border-[#1E3A8A]/30 animate-ping"
              style={{ animationDuration: '3s' }}
            />
            <div
              className="absolute w-48 h-48 rounded-full border border-[#1E3A8A]/15 animate-ping"
              style={{ animationDuration: '4s', animationDelay: '0.5s' }}
            />

            {/* Core funnel circle */}
            <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#0B1120] border-2 border-[#1E3A8A]/60 flex flex-col items-center justify-center shadow-[0_0_60px_rgba(30,58,138,0.4)]">
              <div className="w-10 h-10 flex items-center justify-center mb-1">
                <i className="ri-filter-3-fill text-3xl text-white/90"></i>
              </div>
              <span className="text-[10px] text-white/60 font-medium tracking-wider uppercase">
                Funnel Core
              </span>
            </div>
          </div>

          {/* Connection lines (desktop only) */}
          <div className="absolute inset-0 z-10 pointer-events-none hidden lg:block">
            {/* Top-left to center */}
            <div
              className="absolute top-[25%] left-[32%] w-[18%] h-px bg-gradient-to-r from-white/10 to-[#1E3A8A]/30"
              style={{ transform: 'rotate(25deg)' }}
            />
            {/* Top-right to center */}
            <div
              className="absolute top-[25%] right-[32%] w-[18%] h-px bg-gradient-to-l from-white/10 to-[#1E3A8A]/30"
              style={{ transform: 'rotate(-25deg)' }}
            />
            {/* Bottom-left to center */}
            <div
              className="absolute bottom-[25%] left-[32%] w-[18%] h-px bg-gradient-to-r from-white/10 to-[#1E3A8A]/30"
              style={{ transform: 'rotate(-25deg)' }}
            />
            {/* Bottom-right to center */}
            <div
              className="absolute bottom-[25%] right-[32%] w-[18%] h-px bg-gradient-to-l from-white/10 to-[#1E3A8A]/30"
              style={{ transform: 'rotate(25deg)' }}
            />
          </div>

          {/* Platform Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-x-64 lg:gap-y-8">
            {platforms.map((platform) => (
              <div
                key={platform.id}
                className={`group relative bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 transition-all duration-500 cursor-default ${platform.borderHover} hover:bg-white/[0.06] hover:-translate-y-1 hover:shadow-[0_8px_40px_rgba(0,0,0,0.3)]`}
                onMouseEnter={() => setHoveredId(platform.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Glow effect on hover */}
                <div
                  className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${platform.bgGlow} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                />

                <div className="relative z-10">
                  {/* Icon row */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center border border-white/10 group-hover:border-white/20 transition-colors">
                      <i
                        className={`${platform.icon} text-2xl text-white/80 group-hover:text-white transition-colors`}
                      ></i>
                    </div>
                    {platform.secondIcon && (
                      <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center border border-white/10 group-hover:border-white/20 transition-colors">
                        <i
                          className={`${platform.secondIcon} text-2xl text-white/80 group-hover:text-white transition-colors`}
                        ></i>
                      </div>
                    )}
                  </div>

                  {/* Platform name */}
                  <h3 className="text-xl font-bold text-white mb-1">{platform.name}</h3>
                  <p className="text-white/40 text-sm mb-4">{platform.subtitle}</p>

                  {/* Tag */}
                  <div
                    className={`inline-flex items-center gap-1.5 ${platform.tagBg} ${platform.tagText} px-3 py-1.5 rounded-full text-xs font-semibold mb-5`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-crosshair-2-line text-xs"></i>
                    </div>
                    {platform.tag}
                  </div>

                  {/* Description */}
                  <p className="text-white/50 text-sm leading-relaxed group-hover:text-white/70 transition-colors">
                    {platform.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom data flow bar */}
        <div className="mt-20 flex flex-col items-center">
          <div className="relative w-full max-w-2xl h-px">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#1E3A8A]/50 to-transparent" />
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-[#1E3A8A] to-transparent animate-pulse"
              style={{ animationDuration: '3s' }}
            />
            {/* Data nodes */}
            <div className="absolute top-1/2 left-[20%] -translate-y-1/2 w-2 h-2 rounded-full bg-[#1E3A8A] shadow-[0_0_8px_rgba(30,58,138,0.8)]" />
            <div className="absolute top-1/2 left-[45%] -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#1E3A8A]/70 shadow-[0_0_6px_rgba(30,58,138,0.6)]" />
            <div className="absolute top-1/2 left-[70%] -translate-y-1/2 w-2 h-2 rounded-full bg-[#1E3A8A] shadow-[0_0_8px_rgba(30,58,138,0.8)]" />
          </div>
          <p className="text-white/30 text-sm mt-6 tracking-wider">
            四大平台 &times; 單一儀表板 = 全通路流量掌控
          </p>
        </div>
      </div>
    </section>
  );
}
